import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import * as FileSaver from 'file-saver';

import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

import { GET, GET_ONE, provider, authHeader } from '../_utils';
import Playlist from './Playlist';
import {
    callDir,
    formatPhone,
    formatWhen,
    mmss,
    isoToSeconds,
    formatTrackStart,
    recordId,
    recordPhone,
    recordAgent,
    recordQueue,
    recordWhen,
    readNotes,
    writeNotes,
} from './utils';

const Field = ({ k, v, mono, wide }) => (
    <div className="f" style={wide ? { gridColumn: '1 / -1' } : undefined}>
        <div className="k">{k}</div>
        <div className={`v${mono ? ' m' : ''}${v && v !== '—' ? '' : ' empty'}`}>
            {v && v !== '—' ? v : 'не заполнено'}
        </div>
    </div>
);

const CRM_STATUS = {
    Connected: 'Соединён',
    Complited: 'Завершён',
    Planned: 'Запланирован',
    Callback: 'Обратный звонок',
    'Not Connected': 'Не соединён',
};

const crmStatusLabel = (status) => {
    if (!status) return '';
    return CRM_STATUS[status] || String(status);
};

const pickCrmCard = (rows) => {
    if (!Array.isArray(rows) || !rows.length) return null;
    const row = rows.find((item) => (
        item && (item.task || item.sub_task || item.result || item.description || item.crmStatus || item.queue)
    )) || rows[0];
    return {
        task: row.task || '',
        sub_task: row.sub_task || '',
        result: row.result || '',
        description: row.description || '',
        queue: row.queue,
        crmStatus: row.crmStatus || '',
    };
};

const hasCardLabel = (fields, label) => (
    (fields || []).some((item) => item && item.label === label)
);

const CallShow = ({
    record,
    onPrev,
    onNext,
    flagged,
    onFlag,
    onPlayed,
    onTimeupdate,
    cardFields = [],
    hidePlayer = false,
    allowDownload = true,
    onClose,
}) => {
    const [tracks, setTracks] = useState([]);
    const [current, setCurrent] = useState(0);
    const [uri, setUri] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [tracksLoading, setTracksLoading] = useState(!hidePlayer);
    const [mediaLoading, setMediaLoading] = useState(!hidePlayer);
    const [regions, setRegions] = useState(null);
    const [chunks, setChunks] = useState([]);
    const [activeNode, setActiveNode] = useState(null);
    const [summary, setSummary] = useState('');
    const [note, setNote] = useState('');
    const [noteSaved, setNoteSaved] = useState(true);
    const [linkHint, setLinkHint] = useState('');
    const [crmCard, setCrmCard] = useState(null);

    const uid = record && record.uid;
    const sensyId = record && record.sensyId;
    const id = recordId(record);
    const fallbackCallType = record && (record.callType != null ? record.callType : record.CallType);
    const fallbackDestination = record && (record.destination || record.DestName);
    const fallbackDuration = record ? isoToSeconds(record.duration != null ? record.duration : record.Duration) : 0;
    const fallbackMedia = record && (record.mediaFileAvailable != null ? record.mediaFileAvailable : record.IsMediaFileAvailable);
    const fallbackOriginator = record && (record.originator || record.OrgName);
    const fallbackStart = record && formatTrackStart(record.start || record.StartTime || record.callTime);
    const fallbackTrack = useMemo(() => {
        if (hidePlayer || (id == null && !uid)) return null;
        return {
            callType: fallbackCallType,
            destination: fallbackDestination,
            duration: fallbackDuration,
            id,
            mediaFileAvailable: fallbackMedia,
            originator: fallbackOriginator,
            start: fallbackStart,
            uid: uid,
        };
    }, [
        hidePlayer,
        id,
        uid,
        fallbackCallType,
        fallbackDestination,
        fallbackDuration,
        fallbackMedia,
        fallbackOriginator,
        fallbackStart,
    ]);

    useEffect(() => {
        let cancelled = false;
        setTracks([]);
        setCurrent(0);
        setUri(null);
        setFileName(null);
        setRegions(null);
        setChunks([]);
        setActiveNode(null);
        setSummary('');
        setLinkHint('');
        setCrmCard(null);
        setTracksLoading(!hidePlayer);
        setMediaLoading(!hidePlayer);

        const notes = readNotes();
        setNote(id && notes[id] ? notes[id] : '');
        setNoteSaved(true);

        const fetchTracks = async () => {
            if (hidePlayer) {
                if (!cancelled) setTracksLoading(false);
                return;
            }
            const fallback = fallbackTrack ? [fallbackTrack] : [];
            if (!uid) {
                if (!cancelled) {
                    setTracks(fallback);
                    setTracksLoading(false);
                }
                return;
            }
            try {
                const { data } = await provider(GET, `/visorList`, { id: uid });
                if (cancelled) return;
                const rows = (data && data.data) || [];
                const next = rows.map((element) => ({
                    callType: element.callType,
                    destination: element.destination,
                    duration: isoToSeconds(element.duration),
                    id: element.id,
                    mediaFileAvailable: element.mediaFileAvailable,
                    originator: element.originator,
                    start: formatTrackStart(element.start),
                    uid: element.uid || uid,
                }));
                next.sort((a, b) => moment(a.start).diff(moment(b.start)));
                if (!cancelled) {
                    setTracks(next.length ? next : fallback);
                    setCrmCard(pickCrmCard(rows));
                }
            } catch (e) {
                if (!cancelled) setTracks(fallback);
            }
            if (!cancelled) setTracksLoading(false);
        };

        const fetchSensy = async () => {
            if (!sensyId) return;
            const { data, status } = await provider(GET_ONE, `/sensy/result/`, sensyId);
            if (cancelled || status !== 200 || !data) return;
            setRegions({
                tonal: data.tonal && data.tonal.regions,
                simultaneous: data.simultaneousSpeech && data.simultaneousSpeech.regions,
                silence: data.simultaneousSilence && data.simultaneousSilence.regions,
                keyWords: data.keywordsSearchResult && data.keywordsSearchResult.regions,
            });
            if (data.stt && data.stt.chunks) {
                data.stt.chunks.forEach((chunk, index) => {
                    const tonalsRegions = ((data.tonal && data.tonal.regions) || []).filter(
                        (tonal) => tonal.startTime >= chunk.startTime && tonal.endTime <= chunk.endTime && chunk.channel === tonal.channel
                    );
                    data.stt.chunks[index].avgTonal = tonalsRegions.length
                        ? tonalsRegions.reduce((r, o) => r + o.prob, 0) / tonalsRegions.length
                        : 0;
                });
                setChunks(data.stt.chunks);
            }
            if (data.gptSummary) setSummary(data.gptSummary);
        };

        fetchTracks();
        fetchSensy();
        return () => { cancelled = true; };
    }, [uid, sensyId, id, hidePlayer, fallbackTrack]);

    const track = tracks[current];

    useEffect(() => {
        let cancelled = false;
        setUri(null);
        setFileName(null);
        const currentTrack = tracks[current];
        if (hidePlayer) {
            setMediaLoading(false);
            return undefined;
        }
        if (tracksLoading) {
            setMediaLoading(true);
            return undefined;
        }
        if (!currentTrack || !currentTrack.id || !currentTrack.uid) {
            setMediaLoading(false);
            return undefined;
        }
        setMediaLoading(true);
        const loadMedia = async () => {
            try {
                const params = new URLSearchParams({
                    id: String(currentTrack.id),
                    uid: String(currentTrack.uid),
                });
                if (currentTrack.start) params.set('start', currentTrack.start);
                if (currentTrack.duration != null) params.set('duration', String(currentTrack.duration));
                const response = await fetch(`/api/visor/media/?${params.toString()}`, {
                    method: 'get',
                    headers: { ...authHeader(), 'Content-Type': 'application/json' },
                });
                if (cancelled) return;
                if (response.status === 200) {
                    const parsed = JSON.parse(await response.text());
                    setFileName(parsed.fileName || null);
                    setUri(parsed.fileName ? `/api/visor/play/${parsed.fileName}` : null);
                }
            } catch (e) { /* skip */ }
            if (!cancelled) setMediaLoading(false);
        };
        loadMedia();
        return () => { cancelled = true; };
    }, [tracks, current, hidePlayer, tracksLoading]);
    const dir = callDir(track || record);
    const phone = formatPhone(recordPhone(record) || (track && (dir === 'in' ? track.originator : track.destination)));
    const agent = recordAgent(record) || (track && track.destination) || '—';
    const when = formatWhen(recordWhen(record) || (track && track.start));
    const dur = mmss((track && track.duration) || (record && record.duration) || 0);
    const queueLabel = recordQueue(crmCard) || recordQueue(record);
    const crmTask = (crmCard && crmCard.task) || (record && record.task);
    const crmSubTask = (crmCard && crmCard.sub_task) || (record && record.sub_task);
    const crmResult = (crmCard && crmCard.result) || (record && record.result);
    const crmDescription = (crmCard && crmCard.description) || (record && record.description);
    const crmStatus = crmStatusLabel((crmCard && crmCard.crmStatus) || (record && record.crmStatus));

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setLinkHint('Скопировано');
            setTimeout(() => setLinkHint(''), 1500);
        } catch (e) {
            setLinkHint('Не удалось скопировать');
        }
    };

    const download = async () => {
        const elem = track;
        if (!elem) return;
        if (fileName) {
            const response = await fetch(`/api/visor/play/${fileName}`, {
                method: 'get',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
            });
            const buffer = await response.arrayBuffer();
            FileSaver.saveAs(new Blob([buffer], { type: 'audio/mpeg' }), fileName);
            return;
        }
        const { data } = await provider(GET, '/visor/media', { id: elem.id, uid: elem.uid });
        if (!data) return;
        const response = await fetch(`/api/visor/play/${data.fileName}`, {
            method: 'get',
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
        });
        const buffer = await response.arrayBuffer();
        FileSaver.saveAs(new Blob([buffer], { type: 'audio/mpeg' }), data.fileName);
    };

    const saveNote = () => {
        if (!id) return;
        const notes = readNotes();
        notes[id] = note;
        writeNotes(notes);
        setNoteSaved(true);
    };

    const scrollFunc = (time) => {
        if (onTimeupdate) onTimeupdate(time);
        const index = chunks.findIndex((element) => element.startTime <= time && element.endTime >= time);
        if (index !== -1 && activeNode !== index) {
            setActiveNode(index);
            const node = document.getElementById(`node-${index}`);
            if (node) node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    };

    if (!record) {
        return (
            <div className="pc-ls-panel">
                <div className="pc-ls-empty">
                    <b>Запись не выбрана</b>
                    Выберите звонок слева.
                </div>
            </div>
        );
    }

    return (
        <div className="pc-ls-panel">
            <div className="pc-ls-head">
                <div style={{ minWidth: 0 }}>
                    <div className="pc-ls-head__ph">{phone}</div>
                    <div className="pc-ls-head__sub">
                        {dir === 'in' ? 'входящий' : 'исходящий'}
                        {queueLabel ? ` · ${queueLabel}` : ''}
                        {` · ${agent}`}
                        {` · ${when}`}
                        {` · ${dur}`}
                    </div>
                    {tracks.length > 1 && (
                        <div className="pc-ls-tracks">
                            {tracks.map((item, i) => (
                                <button
                                    key={item.id || i}
                                    type="button"
                                    className={`pc-ls-btn pc-ls-btn--sm${i === current ? ' pc-ls-btn--pri' : ''}`}
                                    onClick={() => setCurrent(i)}
                                >
                                    Запись {i + 1}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="pc-ls-head__rt">
                    <div className="pc-ls-navbtns">
                        <button type="button" title="Предыдущая" onClick={onPrev}><ChevronLeftIcon sx={{ fontSize: 16 }} /></button>
                        <button type="button" title="Следующая" onClick={onNext}><ChevronRightIcon sx={{ fontSize: 16 }} /></button>
                    </div>
                    <button type="button" className="pc-ls-btn" onClick={copyLink}>
                        <LinkIcon sx={{ fontSize: 14 }} />
                        {linkHint || 'Ссылка'}
                    </button>
                    {allowDownload && !hidePlayer ? (
                    <button type="button" className="pc-ls-btn pc-ls-btn--pri" onClick={download} disabled={!track || mediaLoading}>
                        <DownloadIcon sx={{ fontSize: 14 }} />
                        Скачать
                    </button>
                    ) : null}
                    {onClose ? (
                    <button type="button" className="pc-ls-btn pc-ls-btn--ico" title="Закрыть" onClick={onClose}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </button>
                    ) : null}
                </div>
            </div>

            {!hidePlayer && (
            <Playlist
                url={uri}
                loading={tracksLoading || mediaLoading}
                duration={track && track.duration}
                regionsData={regions}
                chunks={chunks}
                labels={{
                    op: agent,
                    client: formatPhone(recordPhone(record) || '').slice(0, 12) + (formatPhone(recordPhone(record) || '').length > 12 ? '…' : ''),
                }}
                flagged={flagged}
                onFlag={onFlag}
                onTimeupdate={scrollFunc}
                onPlayed={onPlayed}
                onPrev={onPrev}
                onNext={onNext}
            />
            )}

            <div className="pc-ls-cardarea">
                <div className="pc-ls-sect">Карточка звонка</div>
                <div className="pc-ls-dl">
                    <Field k="Телефон" v={phone} mono />
                    <Field k="Оператор" v={agent} />
                    <Field k="Очередь" v={queueLabel} />
                    <Field k="Дата и время" v={when} mono />
                    <Field k="Длительность" v={dur} mono />
                    <Field k="Тип" v={dir === 'in' ? 'Входящий' : 'Исходящий'} />
                    {!hasCardLabel(cardFields, 'Статус') ? <Field k="Статус" v={crmStatus} /> : null}
                    {!hasCardLabel(cardFields, 'Тема') ? <Field k="Тема" v={crmTask} /> : null}
                    {!hasCardLabel(cardFields, 'Подтема') ? <Field k="Подтема" v={crmSubTask} /> : null}
                    {!hasCardLabel(cardFields, 'Результат') ? <Field k="Результат" v={crmResult} /> : null}
                    {cardFields.map((item, index) => (
                        <Field
                            key={`${item.label}-${index}`}
                            k={item.label}
                            v={item.value}
                            mono={item.mono}
                            wide={item.wide}
                        />
                    ))}
                    {!hasCardLabel(cardFields, 'Комментарий') ? <Field k="Комментарий" v={crmDescription} wide /> : null}
                    {record.summary ? <Field k="Суть обращения" v={record.summary} wide /> : null}
                    {summary && !record.summary ? <Field k="Суть обращения" v={summary} wide /> : null}
                </div>

                <div className="pc-ls-sect" style={{ marginTop: 18 }}>Заметка супервайзера</div>
                <div className="pc-ls-note">
                    <textarea
                        placeholder="Что отметить по этому звонку"
                        value={note}
                        onChange={(e) => { setNote(e.target.value); setNoteSaved(false); }}
                    />
                    <button
                        type="button"
                        className="pc-ls-btn pc-ls-btn--pri"
                        style={{ alignSelf: 'flex-start' }}
                        onClick={saveNote}
                        disabled={noteSaved}
                    >
                        Сохранить
                    </button>
                </div>

                {chunks.length > 0 && (
                    <>
                        <div className="pc-ls-sect" style={{ marginTop: 18 }}>Расшифровка</div>
                        <div className="pc-ls-tr">
                            {chunks.map((element, i) => (
                                <div
                                    key={i}
                                    id={`node-${i}`}
                                    className={`pc-ls-tr__row${element.channel === 0 ? ' is-op' : ' is-cl'}${activeNode === i ? ' is-cur' : ''}`}
                                >
                                    <div className="pc-ls-tr__who">{element.channel === 0 ? 'Оператор' : 'Клиент'}</div>
                                    <div className="pc-ls-tr__tx">{element.text}</div>
                                    <div className="pc-ls-tr__tm">{moment().startOf('day').add(element.startTime, 'seconds').format('mm:ss')}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CallShow;
